import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = 'https://social-auto-r8we.onrender.com/api/scheduled-posts';

export const createScheduledPost = createAsyncThunk(
    'scheduledPosts/create',
    async (postData, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const formData = new FormData();

            formData.append('platform', postData.platform);
            formData.append('accountId', postData.accountId);
            formData.append('caption', postData.caption || '');
            formData.append('scheduledTime', postData.scheduledTime);
            formData.append('postType', postData.postType || 'image');

            if (postData.media) {
                formData.append('media', postData.media);
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };

            const { data } = await axios.post(API_URL, formData, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create scheduled post');
        }
    }
);

export const getScheduledPosts = createAsyncThunk(
    'scheduledPosts/getAll',
    async (filters = {}, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: filters
            };

            const { data } = await axios.get(API_URL, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch scheduled posts');
        }
    }
);

export const updateScheduledPost = createAsyncThunk(
    'scheduledPosts/update',
    async ({ id, updateData }, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const formData = new FormData();

            if (updateData.caption !== undefined) formData.append('caption', updateData.caption);
            if (updateData.scheduledTime) formData.append('scheduledTime', updateData.scheduledTime);
            if (updateData.media) formData.append('media', updateData.media);

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };

            const { data } = await axios.put(`${API_URL}/${id}`, formData, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update scheduled post');
        }
    }
);

export const deleteScheduledPost = createAsyncThunk(
    'scheduledPosts/delete',
    async (id, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            await axios.delete(`${API_URL}/${id}`, config);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete scheduled post');
        }
    }
);

export const postNow = createAsyncThunk(
    'scheduledPosts/postNow',
    async (id, { getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const { data } = await axios.post(`${API_URL}/${id}/post-now`, {}, config);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to post now');
        }
    }
);

const scheduledPostSlice = createSlice({
    name: 'scheduledPosts',
    initialState: {
        posts: [],
        loading: false,
        error: null
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create
            .addCase(createScheduledPost.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(createScheduledPost.fulfilled, (state, action) => {
                state.loading = false;
                state.posts.unshift(action.payload.post);
            })
            .addCase(createScheduledPost.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            // Get All
            .addCase(getScheduledPosts.pending, (state) => { state.loading = true; })
            .addCase(getScheduledPosts.fulfilled, (state, action) => {
                state.loading = false;
                state.posts = action.payload.data || [];
            })
            .addCase(getScheduledPosts.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            // Update
            .addCase(updateScheduledPost.pending, (state) => { state.loading = true; })
            .addCase(updateScheduledPost.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.posts.findIndex(p => p.id === action.payload.post._id);
                if (index !== -1) {
                    state.posts[index] = action.payload.post;
                }
            })
            .addCase(updateScheduledPost.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            // Delete
            .addCase(deleteScheduledPost.fulfilled, (state, action) => {
                state.posts = state.posts.filter(p => p._id !== action.payload);
            })

            // Post Now
            .addCase(postNow.pending, (state) => { state.loading = true; })
            .addCase(postNow.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.posts.findIndex(p => p._id === action.payload.post._id);
                if (index !== -1) {
                    state.posts[index] = action.payload.post;
                }
            })
            .addCase(postNow.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    }
});

export const { clearError } = scheduledPostSlice.actions;
export default scheduledPostSlice.reducer;
